import { Box, Paper } from "@mui/material";
import {
	CellClickedEvent,
	CellValueChangedEvent,
	SelectionChangedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import { CustomLoadingOverlay } from "@/controls/customLoader/CustomCircularProgress";
import { ChemistrySample } from "@/pages/caster/models/ElementData";
import { useTableStyles } from "@/pages/heat-report/styles/useTableStyles";
import { useElementTableData } from "./hooks/useElementTableData";
import { useElementStyles } from "./styles/useElementStyles";

interface Props {
	elementList: ChemistrySample[];
	coloringEnabled: boolean;
	onToggleColoring: (enabled: boolean) => void;
	editable?: boolean;
	loading?: boolean;
	onCellValueChanged?: (params: CellValueChangedEvent) => void;
	onCellEditingStarted?: () => void;
	onCellEditingStopped?: () => void;
	onSelectionChanged?: (params: SelectionChangedEvent) => void;
	onCellClicked?: (params: CellClickedEvent) => void;
	onAddSample?: () => void;
	gradeId?: number | null;
	onDeleteSample?: (chemistrySampleId: number) => void;
	isSampleTooOld?: (reportedDate: Date | null) => boolean;
}

export const ElementTable = ({
	elementList,
	coloringEnabled,
	onToggleColoring,
	editable = false,
	loading = false,
	onCellValueChanged,
	onCellEditingStarted,
	onCellEditingStopped,
	onSelectionChanged,
	onCellClicked,
	onAddSample,
	gradeId,
	onDeleteSample,
	isSampleTooOld,
}: Props) => {
	const commonTableClasses = useTableStyles();
	const localClasses = useElementStyles();

	const { columns, rowData, getRowStyle, frameworkComponents, statusBar } =
		useElementTableData({
			elementList,
			coloringEnabled,
			onToggleColoring,
			editable,
			onAddSample,
			gradeId,
			onDeleteSample,
			onCellClicked,
			isSampleTooOld,
		});

	return (
		<>
			<Paper
				className={commonTableClasses.borderAgGridTable}
				elevation={0}
				style={{ margin: "10px 0px" }}
			>
				<Box p={0}>
					<div
						className={localClasses.gridWrapper}
						style={{
							minWidth: "100%",
						}}
					>
						<AgGridReact
							rowData={rowData}
							columnDefs={columns}
							domLayout="autoHeight"
							suppressRowClickSelection={true}
							rowSelection="single"
							suppressPaginationPanel={true}
							suppressContextMenu
							getRowId={(params) => params.data.id}
							getRowStyle={getRowStyle}
							noRowsOverlayComponent={() => "No chemistry data available"}
							loadingOverlayComponent={CustomLoadingOverlay}
							loading={loading}
							components={frameworkComponents}
							statusBar={statusBar}
							onCellValueChanged={onCellValueChanged}
							onCellEditingStarted={onCellEditingStarted}
							onCellEditingStopped={onCellEditingStopped}
							onSelectionChanged={onSelectionChanged}
							onCellClicked={onCellClicked}
							stopEditingWhenCellsLoseFocus={true}
						/>
					</div>
				</Box>
			</Paper>
		</>
	);
};
